export type TodoStatus =
  | "Not Submitted"
  | "Awaiting Response"
  | "Waiting on Others"
  | "Completed"
  | "Requires a Change"
  | "Submitted for Review";

export type TodoType = "Form" | "Assigned Task";

export type Placement = "Onboarding" | "Application";

export type PlacementRequest = "onboarding" | "application";

export type TodoCompletionStrategy = "on_submission" | "on_review" | "on_delivery" | "custom";

export interface Submission {
  id: string;
  okta_id: string;
  application_id: string;
  order_id?: string;
  form_name: string;
  form_version?: string;
  submission_data: Record<string, unknown>;
  formio_submission_id?: string;
  formio_path?: string;
  feedback?: Record<string, unknown>;
  feedback_html?: string;
  status?: string;
  todo_id?: string;
  archived_at?: string;
  created_at?: string;
  updated_at?: string;
  updated_by?: string;
}

export interface TodoSubmission {
  session_id?: string;
  session_todo_id?: string;
  todo_id: string;
  title: string;
  display_name?: string;
  participant_id: string;
  application_id: string;
  todo_type: TodoType;
  status: TodoStatus;
  permitted_statuses: TodoStatus[];
  ranking?: number;
  due_date?: string;
  required: boolean;
  completion_strategy: TodoCompletionStrategy;
  instructions?: Record<string, unknown>;
  instructions_html?: string;
  supporting_url?: string;
  placement: Placement;
  reviewer_id?: string;
  reviewer_email?: string;
  submissions: Submission[];
  pdf_source_s3_bucket?: string;
  pdf_source_s3_key?: string;
  web_form_url?: string;
  archived_at?: string;
  allow_edit_after_submission: boolean;
  lock_after_due_date: boolean;
}

export type TileKey = "notAccepted" | "notSubmitted" | "pending" | "completed";

export type StatusGroupKey = TileKey | "archived";

export interface StatusTileConfig {
  key: TileKey;
  label: string;
  barClass: string;
  badgeBgClass: string;
  badgeTextClass: string;
}

export interface FilterableTodosSectionProps {
  todos: TodoSubmission[];
  loading?: boolean;
  canViewSubmissions?: boolean;
  studentOktaId?: string;
  applicationId?: string;
}

export const STATUS_DISPLAY_MAP: Record<TodoStatus, string> = {
  "Not Submitted": "Not Submitted",
  "Requires a Change": "Not Accepted",
  "Submitted for Review": "Needs Review",
  "Awaiting Response": "Awaiting Response",
  "Waiting on Others": "Awaiting Delivery",
  Completed: "Completed",
};

export const SESSION_TODO_EDIT_MESSAGE =
  "Session-assigned to-dos can't be edited here. Edit these details on the program session page.";

export const SELECTABLE_STATUS_ORDER: TodoStatus[] = [
  "Waiting on Others",
  "Awaiting Response",
  "Submitted for Review",
  "Completed",
];

export const LOCKED_STATUS: TodoStatus = "Requires a Change";

export const STATUS_DOT_CLASSES: Record<TodoStatus, string> = {
  "Not Submitted": "bg-info-900",
  "Requires a Change": "bg-red-600",
  "Submitted for Review": "bg-warning-900",
  "Awaiting Response": "bg-warning-900",
  "Waiting on Others": "bg-warning-900",
  Completed: "bg-green-600",
};

export const STATUS_GROUP_MAP: Record<TodoStatus, StatusGroupKey> = {
  "Requires a Change": "notAccepted",
  "Not Submitted": "notSubmitted",
  "Awaiting Response": "pending",
  "Waiting on Others": "pending",
  "Submitted for Review": "pending",
  Completed: "completed",
};

export const STATUS_BADGE_CLASSES: Record<string, { bg: string; text: string; outline: string }> = {
  "Requires a Change": { bg: "bg-error-100", text: "text-red-600", outline: "outline-red-600" },
  "Not Submitted": { bg: "bg-gray-25", text: "text-info-900", outline: "outline-info-900" },
  "Waiting on Others": { bg: "bg-warning-100", text: "text-warning-900", outline: "outline-warning-900" },
  "Awaiting Response": { bg: "bg-warning-100", text: "text-warning-900", outline: "outline-warning-900" },
  "Submitted for Review": { bg: "bg-warning-100", text: "text-warning-900", outline: "outline-warning-900" },
  Completed: { bg: "bg-success-100", text: "text-green-600", outline: "outline-green-600" },
};

export const STATUS_TILES: StatusTileConfig[] = [
  {
    key: "notAccepted",
    label: "Not Accepted",
    barClass: "bg-red-600",
    badgeBgClass: "bg-error-100",
    badgeTextClass: "text-red-600",
  },
  {
    key: "notSubmitted",
    label: "Not Submitted",
    barClass: "bg-info-900",
    badgeBgClass: "bg-gray-25",
    badgeTextClass: "text-info-900",
  },
  {
    key: "pending",
    label: "Pending",
    barClass: "bg-warning-900",
    badgeBgClass: "bg-warning-100",
    badgeTextClass: "text-warning-900",
  },
  {
    key: "completed",
    label: "Completed",
    barClass: "bg-green-600",
    badgeBgClass: "bg-success-100",
    badgeTextClass: "text-green-600",
  },
];
